<template>
  <div class="task-calendar">
    <div class="task-calendar__header">
      <themed-button @click="goToToday">Today</themed-button>
    </div>

    <div v-if="loading" class="task-calendar__loading">
      Loading tasks...
    </div>

    <calendar-view
      v-else
      :show-date="showDate"
      :events="calendarEvents"
      :enable-drag-drop="false"
      :show-event-times="false"
      class="theme-default task-calendar__view"
      @click-event="handleEventClick"
      @click-date="handleDateClick"
    >
      <calendar-view-header
        slot="header"
        slot-scope="t"
        :header-props="t.headerProps"
        @input="setShowDate"
      />
    </calendar-view>

    <!-- Selected Date Modal -->
    <themed-modal
      v-if="showDateModal"
      name="date-tasks-modal"
      :title="selectedDateTitle"
      size="md"
      @close="closeDateModal"
    >
      <div class="date-tasks">
        <div v-if="selectedDateTasks.length === 0" class="date-tasks__empty">
          No tasks for this date
        </div>
        <div v-else class="date-tasks__list">
          <div
            v-for="task in selectedDateTasks"
            :key="task.id"
            class="date-task-item"
            @click="handleEditTask(task)"
          >
            <div class="date-task-item__header">
              <h4>{{ task.title }}</h4>
              <span
                class="task-badge"
                :style="{ backgroundColor: getColorValue(task.status?.color || 'gray') }"
              >
                {{ task.status?.name || 'Unknown' }}
              </span>
            </div>
            <div class="date-task-item__meta">
              <span v-if="task.assignee">
                <theme-icon icon="user" type="icons" />
                {{ task.assignee.name }}
              </span>
              <span v-if="task.priority" :style="{ color: getColorValue(task.priority.color) }">
                <theme-icon icon="flag" type="icons" />
                {{ task.priority.name }}
              </span>
            </div>
          </div>
        </div>
        <div class="date-tasks__actions">
          <themed-button @click="handleCreateTaskForDate">
            <theme-icon icon="plus" type="icons" />
            Create Task
          </themed-button>
        </div>
      </div>
    </themed-modal>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { CalendarView, CalendarViewHeader } from 'vue-simple-calendar';
import ThemeIcon from '../ThemeIcon.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemedModal from '../ThemedModal.vue';

import 'vue-simple-calendar/static/css/default.css';

export default {
  name: 'TaskCalendar',

  components: {
    CalendarView,
    CalendarViewHeader,
    ThemeIcon,
    ThemedButton,
    ThemedModal
  },

  emits: ['create-task', 'edit-task'],

  data() {
    return {
      showDate: new Date(),
      selectedDate: null,
      showDateModal: false
    };
  },

  computed: {
    ...mapState('tasks', ['loading']),
    ...mapGetters('tasks', ['tasksForCalendar']),

    calendarEvents() {
      return this.tasksForCalendar.map(task => ({
        id: task.id,
        startDate: task.start_date || task.due_date,
        endDate: task.due_date,
        title: task.title,
        classes: [`task-status-${task.status?.name?.toLowerCase() || 'unknown'}`],
        task: task // Store full task object for later use
      }));
    },

    selectedDateTitle() {
      if (!this.selectedDate) return '';
      return this.selectedDate.toLocaleDateString('en-US', { 
        weekday: 'long', 
        month: 'long', 
        day: 'numeric', 
        year: 'numeric' 
      });
    },

    selectedDateTasks() {
      if (!this.selectedDate) return [];
      const dateStr = this.formatDateKey(this.selectedDate);
      return this.tasksForCalendar.filter(task => {
        // Check if selected date falls within task date range
        const startDateStr = task.start_date || task.due_date;
        const endDateStr = task.due_date;
        
        if (!endDateStr) return false;
        
        const startDateKey = startDateStr.split('T')[0];
        const endDateKey = endDateStr.split('T')[0];
        
        // Task appears on selected date if date is between start and end (inclusive)
        return dateStr >= startDateKey && dateStr <= endDateKey;
      });
    }
  },

  async mounted() {
    await this.loadData();
  },

  methods: {
    async loadData() {
      try {
        await this.$store.dispatch('tasks/fetchTasks');
      } catch (error) {
        console.error('Error loading calendar data:', error);
        this.$notify({
          type: 'error',
          title: 'Error',
          text: 'Failed to load tasks. Please try again.'
        });
      }
    },

    setShowDate(date) {
      this.showDate = date;
    },

    goToToday() {
      this.showDate = new Date();
    },

    handleEventClick(event) {
      if (event.originalEvent && event.originalEvent.task) {
        this.$emit('edit-task', event.originalEvent.task);
      }
    },

    handleDateClick(date) {
      this.selectedDate = date;
      this.showDateModal = true;
      this.$nextTick(() => {
        this.$modal.show('date-tasks-modal');
      });
    },

    closeDateModal() {
      this.$modal.hide('date-tasks-modal');
      this.showDateModal = false;
    },

    handleEditTask(task) {
      this.closeDateModal();
      this.$emit('edit-task', task);
    },

    handleCreateTaskForDate() {
      this.closeDateModal();
      this.$emit('create-task', { dueDate: this.selectedDate });
    },

    formatDateKey(date) {
      if (!date) return '';
      const d = date instanceof Date ? date : new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    getColorValue(color) {
      const colorMap = {
        blue: '#3b82f6',
        yellow: '#eab308',
        green: '#22c55e',
        red: '#ef4444',
        orange: '#f97316',
        gray: '#6b7280'
      };
      return colorMap[color] || color;
    }
  }
};
</script>
